import {
  CreatePhoneNumberRequest,
  PhoneNumberDto,
  phoneNumberEditSchema,
  phoneNumberSchema,
  UpdatePhoneNumberRequest,
} from '@act/shared/data-transfer-objects';
import { FormBuilder } from '@act/storybook';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { userRequests } from 'redux/requests/users.request';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import {
  DialogContentContainer,
  DialogTitleBarComponent,
  normalizeType,
} from './components/SharedDialogHookComponents';

import { StringHelper } from '@act/shared/utilities';
import { Button } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import { ErrorOutline } from '@mui/icons-material';
import {
  createPhoneNumbeUserSuccess,
  getMember,
  updatedPhoneNumberUserSuccess,
} from 'redux/slices/memberSlice.slice';
import { selectScheduledMessagesByMember } from 'redux/slices/messagesSlice.slice';
import { useAppDispatch, useAppSelector } from 'redux/storeHooks';
import { TrackingProvider } from 'react-components/tracking';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import { SelectOption } from 'stories/forms/Select/ControlledSelect';
import { useToast } from '../app/ReusableToast';
import useConfirmationAlertDialog from './hooks/useConfirmationAlertDialog.hook';
import useUnsavedChangesDialog from './hooks/useUnsavedChangesDialog.hook';

export interface PhoneNumberUniqueProps {
  mode: 'Add' | 'Delete' | 'Edit' | 'SetPrimary';
  memberId: string;
  phoneNumbers: PhoneNumberDto[];
}

export type PhoneNumberDialogProps = GenericDialogProps<boolean> &
  PhoneNumberUniqueProps;

export type PhoneNumberDialogCallbackInput = {};

export const PhoneNumberDialog = ({
  mode,
  memberId,
  phoneNumbers,
  open,
  setOpen,
  callback,
}: PhoneNumberDialogProps) => {
  const openConfirmationAlertDialog = useConfirmationAlertDialog();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const scheduledMessages = useAppSelector(
    selectScheduledMessagesByMember(memberId)
  );
  const [selectedPhoneNumber, setSelectedPhoneNumber] =
    useState<PhoneNumberDto>(null);

  const [
    createPhoneNumberResult,
    isCreating,
    createPhoneNumber,
    createPhoneNumberError,
  ] = useApiCall(userRequests.createPhoneNumber);

  const [
    updatePhoneNumberResult,
    isUpdating,
    updatePhoneNumber,
    updatePhoneNumberError,
  ] = useApiCall(userRequests.updatePhoneNumber);

  const [
    deletePhoneNumberResult,
    isDeleting,
    deletePhoneNumber,
    deletePhoneNumberError,
  ] = useApiCall(userRequests.deletePhoneNumber);

  const isLoading = isCreating || isUpdating || isDeleting;
  const schema = mode === 'Add' ? phoneNumberSchema : phoneNumberEditSchema;
  const { control, handleSubmit, reset, watch, formState } = useForm<
    CreatePhoneNumberRequest | UpdatePhoneNumberRequest
  >({
    resolver: zodResolver(schema),
    defaultValues: {
      userId: memberId,
      phoneId: null,
      phoneType: '',
      number: '',
      primary: false,
    },
  });

  const selectedPhoneNumberId = watch('phoneId');
  const formValues = watch();
  const { isDirty } = formState;

  // Compare current form values with selected phone number to determine if actually dirty
  const hasUnsavedChanges = (): boolean => {
    if (mode === 'Add') {
      // For Add mode, check if any fields have been filled
      return (
        formValues.description?.trim() !== '' ||
        formValues.number?.trim() !== ''
      );
    }

    // For Edit/SetPrimary/Delete modes, compare with selected phone number
    if (!selectedPhoneNumber) {
      return isDirty;
    }

    // Compare editable fields with selected phone number
    const currentDescription = formValues.description || '';
    const selectedDescription = selectedPhoneNumber.description || '';
    const currentPrimary = formValues.primary ?? false;
    const selectedPrimary = selectedPhoneNumber.primary ?? false;

    return (
      currentDescription.trim() !== selectedDescription.trim() ||
      currentPrimary !== selectedPrimary
    );
  };

  useEffect(() => {
    if (memberId && mode === 'Add') {
      reset({
        userId: memberId,
        phoneId: null,
        phoneType: 'System generated',
        description: '',
        number: '',
        primary: false,
      });
    }
  }, [memberId, mode, reset]);

  useEffect(() => {
    if (selectedPhoneNumberId) {
      const selectedPhoneNumber = phoneNumbers.find(
        p => p.id === selectedPhoneNumberId
      );
      if (selectedPhoneNumber) {
        setSelectedPhoneNumber(selectedPhoneNumber);
        reset({
          userId: memberId,
          phoneId: selectedPhoneNumber.id,
          phoneType: normalizeType(selectedPhoneNumber.type),
          number: selectedPhoneNumber.number.slice(2),
          description: selectedPhoneNumber.description || '',
          primary: selectedPhoneNumber.primary,
        });
      }
    }
  }, [
    selectedPhoneNumberId,
    phoneNumbers,
    reset,
    memberId,
    setSelectedPhoneNumber,
  ]);

  useEffect(() => {
    if (createPhoneNumberResult && !isCreating) {
      dispatch(
        createPhoneNumbeUserSuccess({
          userId: memberId,
          phoneNumber: createPhoneNumberResult,
        })
      );
      setOpen(false);
      callback(true);
      toast('Phone number has been added successfully');
    }
  }, [
    createPhoneNumberResult,
    createPhoneNumberError,
    isCreating,
    dispatch,
    setOpen,
    callback,
    memberId,
  ]);

  useEffect(() => {
    if (updatePhoneNumberResult && !isUpdating) {
      dispatch(
        updatedPhoneNumberUserSuccess({
          userId: memberId,
          phoneNumberId: updatePhoneNumberResult.id,
          phoneNumber: updatePhoneNumberResult,
        })
      );
      setOpen(false);
      callback(true);
      toast('Phone number has been updated successfully');
    }
  }, [
    updatePhoneNumberResult,
    updatePhoneNumberError,
    isUpdating,
    dispatch,
    setOpen,
    callback,
    memberId,
  ]);

  useEffect(() => {
    if (deletePhoneNumberResult && !isDeleting) {
      dispatch(getMember({ params: { id: memberId } }));
      setOpen(false);
      callback(true);
      toast('Phone number has been deleted successfully');
    }
  }, [
    deletePhoneNumberResult,
    deletePhoneNumberError,
    isDeleting,
    dispatch,
    setOpen,
    callback,
    memberId,
  ]);

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      void handleUnsavedChanges(() => {
        setOpen(false);
        callback(true);
      });
    } else {
      setOpen(false);
      callback(true);
    }
  };

  const phoneNumberOptions: SelectOption<number>[] =
    phoneNumbers?.map(phoneNumber => {
      const type = normalizeType(phoneNumber.type);
      const label = type.concat(
        phoneNumber.description
          ? ` · ${StringHelper.sentenceCase(phoneNumber.description)}`
          : ''
      );

      return {
        key: phoneNumber.id,
        label,
        value: phoneNumber.id,
      };
    }) || [];

  let formConfig: FormConfig<
    CreatePhoneNumberRequest | UpdatePhoneNumberRequest
  > = [];

  if (mode === 'Add') {
    formConfig = [
      {
        type: 'group',
        items: [
          {
            type: 'input',
            name: 'description',
            label: 'Description',
            input: 'text',
            trackingName: 'phone-description-input',
            props: {
              required: true,
              characterLimit: 20,
            },
          },
        ],
      },
      {
        type: 'input',
        name: 'number',
        label: 'Phone number',
        input: 'phone',
        trackingName: 'phone-number-input',
        props: {
          required: true,
        },
      },
    ];
  } else {
    // Delete, Edit, SetPrimary modes
    formConfig.push({
      type: 'input',
      name: 'phoneId',
      label: 'Select an option',
      input: 'select',
      trackingName: 'phone-number-select',
      props: {
        options: phoneNumberOptions,
        fullWidth: true,
        required: true,
      },
    });

    if (selectedPhoneNumber && mode === 'Edit') {
      formConfig.push(
        {
          type: 'group',
          items: [
            {
              type: 'input',
              name: 'phoneType',
              label: 'Phone type',
              input: 'text',
              disabled: true,
              trackingName: 'phone-type-input',
              props: {},
            },
            {
              type: 'input',
              name: 'description',
              label: 'Description',
              input: 'text',
              trackingName: 'phone-description-input',
              props: {
                required: true,
                characterLimit: 20,
              },
            },
          ],
        },
        {
          type: 'input',
          name: 'number',
          label: 'Phone number',
          input: 'phone',
          disabled: true,
          trackingName: 'phone-number-input',
          props: {},
        }
      );
    }
  }

  async function onSubmit(
    data: CreatePhoneNumberRequest | UpdatePhoneNumberRequest
  ) {
    const { description, number, phoneId, primary, userId } = data;

    if (mode === 'Add') {
      createPhoneNumber({
        params: { userId },
        body: {
          ...(description && { description }),
          number: number,
          primary: phoneNumbers?.length === 0,
        },
      });
    }

    if (mode === 'Delete') {
      let message =
        'This phone number will no longer be available to use for this member.';
      const filteredScheduledMessages = scheduledMessages.filter(
        message => message.phoneNumberId === phoneId
      );
      if (filteredScheduledMessages.length > 0) {
        message =
          "Deleting this phone number will also cancel any scheduled texts and permanently remove the number from this member's profile.";
      }
      const confirmDelete = await openConfirmationAlertDialog({
        title: 'Confirm delete',
        icon: <ErrorOutline />,
        iconColor: 'error',
        iconBorderColor: 'errorContainer',
        avatarCaption: 'Are you sure?',
        message,
        confirmationText: 'Yes, delete now',
        confirmButtonColor: 'error',
      });

      if (!confirmDelete) return;

      deletePhoneNumber({
        params: { userId, phoneId },
      });
    }

    if (mode === 'Edit' || mode === 'SetPrimary') {
      updatePhoneNumber({
        params: { userId, phoneId },
        body: {
          ...(description && { description }),
          primary: mode === 'SetPrimary' ? true : primary,
        },
      });
    }
  }

  return (
    <TrackingProvider contextName="phone-number-dialog">
      <Box sx={{ width: '600px' }}>
        <Dialog open={open} onClose={handleClose} fullWidth>
          <DialogContentContainer>
            <DialogTitleBarComponent
              title={`${
                mode === 'SetPrimary' ? 'Set primary' : mode
              } phone number`}
              handleClose={handleClose}
            />
            <DialogContent>
              <FormBuilder
                config={formConfig}
                control={control}
                formState={formState}
              />
            </DialogContent>
            <Divider />
            <DialogActions sx={{ padding: '8px 24px' }}>
              <Button
                onClick={handleClose}
                disabled={isLoading}
                trackingName="cancel-button"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                color={mode === 'Delete' ? 'error' : 'primary'}
                debounced
                onClick={handleSubmit(onSubmit)}
                sx={{ padding: '10px 24px' }}
                disabled={isLoading || (mode === 'Add' && !formState.isValid)}
                startIcon={
                  isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
                trackingName={
                  mode === 'Delete'
                    ? 'delete-phone-button-submit'
                    : 'save-phone-button-submit'
                }
              >
                {isLoading
                  ? mode === 'Delete'
                    ? 'Deleting...'
                    : 'Saving...'
                  : mode === 'Delete'
                  ? 'Delete'
                  : 'Save'}
              </Button>
            </DialogActions>
          </DialogContentContainer>
        </Dialog>
      </Box>
    </TrackingProvider>
  );
};
